<template>
    <v-dialog :value="value" max-width="860" scrollable @input="$emit('input', $event)">
        <v-card class="background-2 round-card">
            <v-card-title class="pa-3">
                <p class="pl-2 pl-xl-3">Assegna automaticamente</p>
                <v-spacer></v-spacer>
                <v-btn icon @click="close"><v-icon>close</v-icon></v-btn>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text class="pt-4" style="max-height: 60vh;">
                <div class="body-2 grey--text mb-3">
                    Proposta ricavata dal <strong>nome del file</strong>: per ogni foto vengono proposte le
                    <strong>varianti</strong> che il nome descrive. Niente viene salvato finché non premi
                    Conferma. Dove il nome non è riconducibile a nessuna variante la riga resta vuota —
                    meglio lasciarla che indovinare.
                </div>

                <v-simple-table dense class="background-2">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th style="width: 40px;"></th>
                                <th>File</th>
                                <th>Varianti proposte</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in rows" :key="row.photo.id">
                                <td>
                                    <v-simple-checkbox
                                        color="primary"
                                        :value="row.include"
                                        @input="$set(row, 'include', $event)"
                                    ></v-simple-checkbox>
                                </td>
                                <td class="body-2" style="max-width: 240px;">
                                    <div class="text-truncate" :title="photoName(row.photo)">{{ photoName(row.photo) }}</div>
                                    <div v-if="row.matched" class="caption success--text">proposta dal nome file</div>
                                    <div v-else-if="row.current.length" class="caption grey--text">assegnazione attuale</div>
                                    <div v-else class="caption grey--text">nessun riscontro</div>
                                </td>
                                <td style="min-width: 320px;">
                                    <v-autocomplete
                                        dense
                                        outlined
                                        hide-details
                                        multiple
                                        small-chips
                                        deletable-chips
                                        clearable
                                        placeholder="—"
                                        :items="variantItems"
                                        :value="row.selection"
                                        @change="pick(row, $event)"
                                    ></v-autocomplete>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>

                <div v-if="!rows.length" class="body-2 grey--text">
                    Nessuna immagine nel File Storage del prodotto.
                </div>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions>
                <span class="body-2 grey--text pl-2">{{ changeCount }} da salvare</span>
                <v-spacer></v-spacer>
                <v-btn text color="info" :disabled="saving" @click="close">Annulla</v-btn>
                <v-btn text color="primary" :loading="saving" :disabled="!changeCount" @click="confirm">Conferma</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    export default {
        name: 'photo-variant-auto-match-dialog',

        props: {
            value: Boolean,
            // Immagini del prodotto con `variant_ids[]` idratato (attachmentslists)
            photos: {
                type: Array,
                default: () => []
            },
            // Righe della tab Varianti (locali incluse: si filtrano gli id reali)
            variants: {
                type: Array,
                default: () => []
            },
            // [{id, name, options:[{text, value}]}] — per riconoscere i valori nel nome
            attributes: {
                type: Array,
                default: () => []
            }
        },

        data() {
            return {
                rows: [],
                saving: false
            }
        },

        computed: {
            // Solo varianti salvate: gli id dummy negativi non possono ricevere
            // assegnazioni (id fantasma)
            savedVariants() {
                return this.variants.filter((variant) => variant.id > 0)
            },

            variantItems() {
                return this.savedVariants.map((variant) => ({
                    text: variant.name || variant.code || ('Variante ' + variant.id),
                    value: variant.id
                }))
            },

            changeCount() {
                return this.rows.filter((row) => row.include && this.isChanged(row)).length
            }
        },

        watch: {
            value(opened) {
                if (opened) this.buildRows()
            }
        },

        methods: {
            photoName(photo) {
                if (!photo) return ''
                if (photo.ext && !String(photo.name).endsWith('.' + photo.ext)) {
                    return photo.name + '.' + photo.ext
                }
                return photo.name
            },

            /**
             * Input:  testo libero (nome file o valore attributo)
             * Fa:     minuscole, accenti rimossi, separatori collassati a spazio
             * Output: stringa confrontabile
             */
            normalize(text) {
                return String(text == null ? '' : text)
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9]+/g, ' ')
                    .trim()
            },

            /**
             * Input:  testo libero
             * Fa:     come `normalize`, ma i separatori spariscono invece di
             *         diventare spazio
             * Output: stringa confrontabile senza separatori
             */
            squash(text) {
                return this.normalize(text).replace(/ /g, '')
            },

            /**
             * Input:  nome file, attributo
             * Fa:     cerca il valore contenuto nel nome; a parita' vince il piu' lungo.
             *         Il confronto gira su DUE forme, con e senza separatori: con la sola
             *         forma a spazi "verdeacqua.jpg" non conteneva "verde acqua" e vinceva
             *         il valore piu' corto "Verde", cioe' l'opposto dell'intento.
             * Output: il `text` del valore riconosciuto, o null se nessun riscontro o
             *         parita' ambigua
             */
            matchValue(fileName, attribute) {
                const haystackLoose = this.normalize(fileName)
                const haystackTight = this.squash(fileName)
                let best = null
                let bestLength = 0
                let ambiguous = false

                ;(attribute.options || []).forEach((option) => {
                    const loose = this.normalize(option.text)
                    const tight = this.squash(option.text)
                    if (!tight) return

                    const found = (loose && haystackLoose.indexOf(loose) !== -1)
                        || haystackTight.indexOf(tight) !== -1
                    if (!found) return

                    if (tight.length > bestLength) {
                        best = option.text
                        bestLength = tight.length
                        ambiguous = false
                    } else if (tight.length === bestLength && option.text !== best) {
                        ambiguous = true
                    }
                })

                return ambiguous ? null : best
            },

            /**
             * Input:  variante, id attributo
             * Output: testo del valore, o null — `values` e' `[]` quando manca,
             *         e in JS `[]` e' truthy, da cui il controllo su Array
             */
            variantValueOf(variant, attributeId) {
                const entry = variant.attributes ? variant.attributes[attributeId] : null
                if (!entry || !entry.values || Array.isArray(entry.values)) return null
                const text = entry.values.text
                return text == null || text === '' ? null : text
            },

            /**
             * Input:  nome file
             * Fa:     riconosce un valore per ciascun attributo e propone le varianti
             *         che li soddisfano TUTTI: "rosso-s.jpg" → la sola Rossa S,
             *         "rosso.jpg" → tutte le rosse (gli attributi non riconosciuti
             *         non vincolano). Nessun valore riconosciuto → nessuna proposta.
             * Output: [variant_id]
             */
            matchVariants(fileName) {
                const constraints = {}
                let any = false
                this.attributes.forEach((attribute) => {
                    const guess = this.matchValue(fileName, attribute)
                    if (guess != null) {
                        constraints[attribute.id] = guess
                        any = true
                    }
                })
                if (!any) return []

                return this.savedVariants
                    .filter((variant) => Object.keys(constraints).every(
                        (attributeId) => this.variantValueOf(variant, attributeId) === constraints[attributeId]
                    ))
                    .map((variant) => variant.id)
            },

            /**
             * Input:  riga, elenco id scelto a mano
             * Fa:     una scelta manuale riaccende la riga: senza, una riga "nessun
             *         riscontro" modificata non verrebbe contata in `changeCount` e
             *         Conferma resterebbe spento senza motivo visibile
             * Output: nessuno
             */
            pick(row, ids) {
                this.$set(row, 'selection', ids)
                this.$set(row, 'include', true)
            },

            buildRows() {
                this.rows = this.photos.map((photo) => {
                    const current = (photo.variant_ids || []).slice().sort((a, b) => a - b)

                    // La proposta non sovrascrive un'assegnazione gia' fatta a mano:
                    // si propone solo per le foto ancora senza legami
                    let selection = current.slice()
                    let matched = false
                    if (!current.length) {
                        const proposed = this.matchVariants(this.photoName(photo))
                        if (proposed.length) {
                            selection = proposed
                            matched = true
                        }
                    }

                    return { photo: photo, current: current, selection: selection, matched: matched, include: matched }
                })
            },

            isChanged(row) {
                const selection = row.selection.slice().sort((a, b) => a - b)
                if (selection.length !== row.current.length) return true
                return selection.some((id, i) => id !== row.current[i])
            },

            confirm() {
                const targets = this.rows.filter((row) => row.include && this.isChanged(row))
                if (!targets.length) return

                this.saving = true
                const calls = targets.map((row) => {
                    const ids = row.selection.slice()
                    return this.axios.post('attachments/variants', {
                        attachment_id: row.photo.id,
                        variant_ids: ids
                    }).then(() => ({ photo_id: row.photo.id, variant_ids: ids }))
                })

                Promise.all(calls).then((results) => {
                    this.saving = false
                    this.$emit('applied', results)
                    this.$eventBusUtility.$emit('variantPhotoAssignmentsChanged')
                    this.$store.commit('setSnackbar', {
                        timeout: 4000,
                        text: results.length + (results.length === 1 ? ' foto assegnata' : ' foto assegnate'),
                        value: true,
                        color: 'success'
                    })
                    this.close()
                }).catch(() => {
                    this.saving = false
                    this.$store.commit('setSnackbar', {
                        timeout: 6000,
                        text: 'Errore nel salvataggio: ricontrolla le assegnazioni',
                        value: true,
                        color: 'error'
                    })
                    this.$emit('failed')
                })
            },

            close() {
                this.$emit('input', false)
            }
        }
    }
</script>
